/**
 * Drivers Bids — every bid a driver placed, newest first.
 *
 * One row per BID, not per load. `load_bids` keeps one row per (load, driver) and a re-bid overwrites the
 * price, which is right for the board — an operator wants to know what this driver is asking NOW. It is
 * wrong for this question: a driver who opened at $150 and moved to $300 is the interesting case, and the
 * $150 is exactly what the board had to throw away. These rows come from `load_bids_history`, which keeps
 * both.
 *
 * Every column is the EVENT: the price, and the lane, the broker and the notes as the DRIVER was reading
 * them when they bid. Nothing here is the load as the board holds it now — a Sylectus edit re-posts a load
 * under a new id, and joining would let that rewrite the context of an old bid.
 *
 * The mirror of Dispatchers Bids, on the same shell, because they are the same read about two
 * populations and a staff member should not have to learn a second page to use it.
 *
 * Backend: GET /v1/gluon/driver-bids (any control-panel operator). Read-only.
 */
import Typography from '@mui/joy/Typography'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import { formatUsdWhole } from '@numeo/types-core'
import type { DriverBidRow } from '@numeo/types-main-mobile'
import { BidListPage } from '@/components/bid-list/BidListPage'
import { Notes, place, when } from '@/components/bid-list/cells'
import { fetchDriverBids } from './api'

const Row = ({ bid }: { bid: DriverBidRow }) => (
    <tr key={bid.id}>
        <td>
            <Typography level="body-sm">{place(bid.originCity, bid.originState)}</Typography>
            <Typography level="body-xs" textColor="text.tertiary">
                → {place(bid.destinationCity, bid.destinationState)}
                {bid.miles ? ` · ${Math.round(bid.miles).toLocaleString('en-US')} mi` : ''}
            </Typography>
        </td>
        <td>
            {/* An unnamed driver is a real one — a pool account nobody has filled in — and the stored
                name is dropped when it is an email, which older bids carried as the write default. The
                account id stays available to anyone chasing a specific driver; printing it here would be
                noise in every other row. */}
            <Typography level="body-sm">{bid.bidderName || 'Unnamed driver'}</Typography>
        </td>
        <td>
            <Typography level="body-sm">{bid.companyName || bid.projectName}</Typography>
            {bid.companyName && (
                <Typography level="body-xs" textColor="text.tertiary">
                    {bid.projectName}
                </Typography>
            )}
        </td>
        <td>
            <Typography level="body-sm">{bid.brokerName || '—'}</Typography>
            {bid.postedRate !== undefined && (
                <Typography level="body-xs" textColor="text.tertiary">
                    posted {formatUsdWhole(bid.postedRate)}
                </Typography>
            )}
        </td>
        <td>
            <Typography level="body-sm" fontWeight="lg">
                {formatUsdWhole(bid.price)}
            </Typography>
        </td>
        <td>
            <Notes text={bid.loadNotes} />
        </td>
        <td>
            <Typography level="body-xs" textColor="text.tertiary">
                {when(bid.placedAt)}
            </Typography>
        </td>
    </tr>
)

export default function DriverBidsPageView() {
    return (
        <BidListPage<DriverBidRow>
            icon={<LocalShippingIcon />}
            title="Drivers Bids"
            subtitle="Every bid a driver placed, with the price they asked for. One row per bid, so a driver who bids again appears twice."
            searchPlaceholder="Driver, broker or city"
            emptyText="No driver bids in this window."
            fetchPage={fetchDriverBids}
            head={
                <>
                    <th style={{ width: 220 }}>Lane</th>
                    <th style={{ width: 190 }}>Driver</th>
                    <th style={{ width: 180 }}>Company</th>
                    <th style={{ width: 180 }}>Broker</th>
                    <th style={{ width: 120 }}>Bid</th>
                    <th>Load notes</th>
                    <th style={{ width: 170 }}>Placed</th>
                </>
            }
            renderRow={(bid) => <Row key={bid.id} bid={bid} />}
        />
    )
}
